import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';

// ── Scroll automatique vers le haut à chaque changement de page ──
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}
import { Toaster } from './components/ui/sonner';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { TenantProvider } from './context/TenantContext';

// Pages
import HomePage from './pages/HomePage';
import ConfigurateurPage from './pages/ConfigurateurPage';
import CataloguePage from './pages/CataloguePage';
import ProductPage from './pages/ProductPage';
import BoutiquePage from './pages/BoutiquePage';
import BoutiqueSuccesPage from './pages/BoutiqueSuccesPage';
import PanierPage from './pages/PanierPage';
import DevisPage from './pages/DevisPage';
import PaiementSuccesPage from './pages/PaiementSuccesPage';
import UniversPage from './pages/UniversPage';
import BlogPage from './pages/BlogPage';
import ArticlePage from './pages/ArticlePage';
import ContactPage from './pages/ContactPage';
import NousRejoindrePage from './pages/NousRejoindrePage';
import LoginPage from './pages/LoginPage';
import SetPasswordPage from './pages/SetPasswordPage';
import MonComptePage from './pages/MonComptePage';
import SEOLocalPage from './pages/SEOLocalPage';
import GaleriePage from './pages/GaleriePage';

// Admin
import AdminLayout from './admin/AdminLayout';
// Pilotage — Mission 3, Lot 3
import PilotageLayout from './adminv2/PilotageLayout';
import Aujourdhui from './adminv2/Aujourdhui';
import PilotageDossiers from './adminv2/Dossiers';
import FicheDossier from './adminv2/FicheDossier';
import Factures from './adminv2/Factures';
import Partenaires from './adminv2/Partenaires';
import Commissions from './adminv2/Commissions';
import AdminDevis from './admin/AdminDevis';
import AdminClients from './admin/AdminClients';
import AdminIA from './admin/AdminIA';
import AdminBlog from './admin/AdminBlog';
import AdminParametres from './admin/AdminParametres';
import AdminExports from './admin/AdminExports';
import AdminSecurity from './admin/AdminSecurity';
import AdminNotificationsConfig from './admin/AdminNotificationsConfig';
import AdminCE from './admin/AdminCE';
import AdminIAAgent from './admin/AdminIAAgent';
import AdminAnalytics from './admin/AdminAnalytics';
import AdminCRM from './admin/AdminCRM';
import AdminRelancesAuto from './admin/AdminRelancesAuto';
import AdminMessages from './admin/AdminMessages';
import AdminDisponibilites from './admin/AdminDisponibilites';
import AdminExploitation from './admin/AdminExploitation';
import AdminSAV from './admin/AdminSAV';
import AdminTournees from './admin/AdminTournees';
import AdminPlaceToLocal from './admin/AdminPlaceToLocal';
import AdminBoutique from './admin/AdminBoutique';
import AdminPhotoboothV2 from './admin/AdminPhotoboothV2';
// Nouveaux modules
import AdminCommerceHub from './admin/AdminCommerceHub';
import AdminOperationsHub from './admin/AdminOperationsHub';
import AdminConfigurationHub from './admin/AdminConfigurationHub';
import AdminTarifs from './admin/AdminTarifs';
import AdminFidelite from './admin/AdminFidelite';
import AdminLivraisons from './admin/AdminLivraisons';
import AdminMissions from './admin/AdminMissions';
import AdminDocuments from './admin/AdminDocuments';
import AdminAvis from './admin/AdminAvis';
// Pages précédemment non importées — ajoutées Phase A
import AdminAgenda from './admin/AdminAgenda';
import AdminAutomations from './admin/AdminAutomations';
import AdminInspections from './admin/AdminInspections';
import AdminLiveChat from './admin/AdminLiveChat';
import AdminMateriel from './admin/AdminMateriel';
import AdminNotifications from './admin/AdminNotifications';
import AdminOpportunites from './admin/AdminOpportunites';
import AdminRelances from './admin/AdminRelances';
import AdminWhiteLabel from './admin/AdminWhiteLabel';
import AdminDispoWeekend from './admin/AdminDispoWeekend';
import AdminPartenairesConfig from './admin/AdminPartenairesConfig';
import AdminPromo from './admin/AdminPromo';
import AdminCatalogueUnifie from './admin/AdminCatalogueUnifie';
import AdminSEO from './admin/AdminSEO';
import AdminUsers from './admin/AdminUsers';
import GuideMariagePage from './pages/GuideMariagePage';
import GuideAnniversaireEnfantPage from './pages/GuideAnniversaireEnfantPage';
import GuideEntreprisePage from './pages/GuideEntreprisePage';
import GuideBaptemePage from './pages/GuideBaptemePage';
import GuideFeteLocalePage from './pages/GuideFeteLocalePage';
import ShowroomPage from './pages/ShowroomPage';
import PlanDeTablePage from './pages/PlanDeTablePage';
import SuiviLivraisonPage from './pages/SuiviLivraisonPage';
import PublicRsvpPage from './pages/PublicRsvpPage';

// V2 Event OS
import MesEvenementsPage from './pages/MesEvenementsPage';
import MonEvenementPage from './pages/MonEvenementPage';
import InvitePage from './pages/InvitePage';
import TopAmbassadeursPage from './pages/TopAmbassadeursPage';
import LiveChatWidget from './components/common/LiveChatWidget';
import ShareAndEarnWidget from './components/common/ShareAndEarnWidget';
import PacksPageV2 from './pages/PacksPageV2';
import PhotoboothEventPage from './pages/PhotoboothEventPage';
import PromoPopup from './components/common/PromoPopup';
import FabricationPage from './pages/FabricationPage';
import PartenairesPage from './pages/PartenairesPage';
import PrestationsPage from './pages/PrestationsPage';
import ThematiquePage from './pages/ThematiquePage';
import GuidesPage from './pages/GuidesPage';
import GuideAnniversaireAdultePage from './pages/GuideAnniversaireAdultePage';
import PrestataireRecommandesPage from './pages/PrestataireRecommandesPage';
import DevenirPartenairePage from './pages/DevenirPartenairePage';
import PartenairePage from './pages/PartenairePage';
import MonOrganisationPage from './pages/MonOrganisationPage';
import PlanEditorPage from './pages/PlanEditorPage';
import GuestsPage from './pages/GuestsPage';
import InvitationsPage from './pages/InvitationsPage';

// Livreur
import LivreurApp from './pages/LivreurApp';
import FicheMissionPage from './pages/FicheMissionPage';
import MaReservationPage from './pages/MaReservationPage';
import AvisPage from './pages/AvisPage';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function App() {
  // Tracking lien parrainage (?ref=CODE) au landing
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('ose_ref_code', ref);
      fetch(`${BACKEND}/api/parrainage/track-click/${ref}`, { method: 'POST' }).catch((err) => { /* eslint-disable-line no-empty */ });
    }
  }, []);
  return (
    <BrowserRouter>
      <TenantProvider>
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <Routes>
            {/* Site public */}
            {/* Site public */}
            <Route path="/" element={<><HomePage /><PromoPopup /></>} />
            <Route path="/creer-evenement" element={<ConfigurateurPage />} />
            <Route path="/prestations" element={<UniversPage />} />
            <Route path="/univers/:slug" element={<UniversPage />} />
            <Route path="/anniversaires" element={<UniversPage univers="anniversaire" />} />
            <Route path="/mariages" element={<UniversPage univers="mariage" />} />
            <Route path="/professionnels" element={<UniversPage univers="entreprise" />} />
            <Route path="/jeux-animations" element={<UniversPage univers="jeux" />} />
            <Route path="/animations-gourmandes" element={<UniversPage univers="gourmand" />} />
            <Route path="/catalogue" element={<CataloguePage />} />
            {/* Route slug SEO (ex: /produit/location-photobooth-figeac) */}
            <Route path="/produit/:id" element={<ProductPage />} />
            {/* Alias pour compatibilité ancienne URL UUID */}
            <Route path="/boutique" element={<BoutiquePage />} />
            <Route path="/boutique/succes" element={<BoutiqueSuccesPage />} />
            <Route path="/photobooth/galerie/:token" element={<PhotoboothEventPage />} />
            <Route path="/panier" element={<PanierPage />} />
            <Route path="/devis/:id" element={<DevisPage />} />
            <Route path="/devis" element={<DevisPage />} />
            <Route path="/paiement/succes" element={<PaiementSuccesPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<ArticlePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/nous-rejoindre" element={<NousRejoindrePage />} />
            <Route path="/connexion" element={<LoginPage />} />
            <Route path="/definir-mot-de-passe" element={<SetPasswordPage />} />
            <Route path="/guides" element={<GuidesPage />} />
            <Route path="/guide/anniversaire-adulte" element={<GuideAnniversaireAdultePage />} />
            <Route path="/guide/mariage" element={<GuideMariagePage />} />
            <Route path="/guide/anniversaire-enfant" element={<GuideAnniversaireEnfantPage />} />
            <Route path="/guide/entreprise" element={<GuideEntreprisePage />} />
            <Route path="/guide/bapteme" element={<GuideBaptemePage />} />
            <Route path="/guide/fete-locale" element={<GuideFeteLocalePage />} />
            <Route path="/showroom" element={<ShowroomPage />} />
            <Route path="/plan-de-table" element={<PlanDeTablePage />} />
            <Route path="/plan-de-table/:token" element={<PlanDeTablePage />} />
            <Route path="/prestataires-recommandes" element={<PrestataireRecommandesPage />} />
            <Route path="/devenir-partenaire" element={<DevenirPartenairePage />} />
            <Route path="/partenaire" element={<PartenairePage />} />
            <Route path="/mon-organisation" element={<MonOrganisationPage />} />
            <Route path="/mon-organisation/plan-de-table/editeur" element={<PlanEditorPage />} />
            <Route path="/mon-organisation/plan-de-table/invites" element={<GuestsPage />} />
            <Route path="/mon-organisation/plan-de-table/invitations" element={<InvitationsPage />} />
            <Route path="/tracking/:trackingToken" element={<SuiviLivraisonPage />} />
            <Route path="/rsvp/:rsvp_token" element={<PublicRsvpPage />} />
            <Route path="/mon-compte" element={<MonComptePage />} />
            <Route path="/espace-pro" element={<Navigate to="/mon-compte" replace />} />
            <Route path="/espace-association" element={<Navigate to="/mon-compte" replace />} />
            <Route path="/galerie/:orderId" element={<GaleriePage />} />
            <Route path="/packs" element={<PacksPageV2 />} />
            <Route path="/fabrication" element={<FabricationPage />} />
            <Route path="/partenaires" element={<PartenairesPage />} />
            <Route path="/nos-prestations" element={<PrestationsPage />} />
            <Route path="/nos-prestations/:famille" element={<PrestationsPage />} />
            {/* Pages thématiques dédiées */}
            <Route path="/mariage-lot" element={<ThematiquePage theme="mariage" />} />
            <Route path="/location-materiel-mariage-lot" element={<ThematiquePage theme="mariage" />} />
            <Route path="/animations-entreprise-lot" element={<ThematiquePage theme="entreprise" />} />
            <Route path="/anniversaire-enfant-lot" element={<ThematiquePage theme="anniversaire" />} />

            {/* SEO Local Pages — Phase 5 (15 pages) */}
            <Route path="/anniversaire-enfant-figeac" element={<SEOLocalPage slug="anniversaire-enfant-figeac" />} />
            <Route path="/jeux-gonflables-figeac" element={<SEOLocalPage slug="jeux-gonflables-figeac" />} />
            <Route path="/animation-entreprise-lot" element={<SEOLocalPage slug="animation-entreprise-lot" />} />
            <Route path="/photobooth-mariage-lot" element={<SEOLocalPage slug="photobooth-mariage-lot" />} />
            {/* 10 nouvelles pages Phase 5 */}
            <Route path="/anniversaire-adulte-figeac" element={<SEOLocalPage slug="anniversaire-adulte-figeac" />} />
            <Route path="/anniversaire-enfant-capdenac" element={<SEOLocalPage slug="anniversaire-enfant-capdenac" />} />
            <Route path="/location-photobooth-figeac" element={<SEOLocalPage slug="location-photobooth-figeac" />} />
            <Route path="/location-jeux-gonflables-decazeville" element={<SEOLocalPage slug="location-jeux-gonflables-decazeville" />} />
            <Route path="/animation-mariage-lot" element={<SEOLocalPage slug="animation-mariage-lot" />} />
            <Route path="/animation-entreprise-figeac" element={<SEOLocalPage slug="animation-entreprise-figeac" />} />
            <Route path="/bar-a-bonbons-lot" element={<SEOLocalPage slug="bar-a-bonbons-lot" />} />
            <Route path="/location-materiel-evenementiel-figeac" element={<SEOLocalPage slug="location-materiel-evenementiel-figeac" />} />
            <Route path="/animation-commerciale-lot" element={<SEOLocalPage slug="animation-commerciale-lot" />} />
            <Route path="/location-mini-golf-evenementiel" element={<SEOLocalPage slug="location-mini-golf-evenementiel" />} />
            {/* Nouvelles pages SEO villes Phase 10 */}
            <Route path="/location-evenementielle-cahors" element={<SEOLocalPage slug="location-evenementielle-cahors" />} />
            <Route path="/location-evenementielle-saint-cere" element={<SEOLocalPage slug="location-evenementielle-saint-cere" />} />
            <Route path="/animation-mariage-aveyron" element={<SEOLocalPage slug="animation-mariage-aveyron" />} />
            <Route path="/location-chapiteau-barnum-lot" element={<SEOLocalPage slug="location-chapiteau-barnum-lot" />} />
            {/* Nouvelles pages villes Phase 11 */}
            <Route path="/animations-figeac" element={<SEOLocalPage slug="animations-figeac" />} />
            <Route path="/anniversaire-capdenac-gare" element={<SEOLocalPage slug="anniversaire-capdenac-gare" />} />
            <Route path="/location-materiel-decazeville" element={<SEOLocalPage slug="location-materiel-decazeville" />} />
            <Route path="/animation-villefranche-rouergue" element={<SEOLocalPage slug="animation-villefranche-rouergue" />} />
            <Route path="/location-evenementielle-rodez" element={<SEOLocalPage slug="location-evenementielle-rodez" />} />
            <Route path="/location-evenementielle-aurillac" element={<SEOLocalPage slug="location-evenementielle-aurillac" />} />

            {/* Admin */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/pilotage/aujourdhui" replace />} />
              <Route path="catalogue" element={<Navigate to="/admin/catalogue-unifie?tab=ia" replace />} />
              <Route path="catalogue-unifie" element={<AdminCatalogueUnifie />} />
              <Route path="devis" element={<AdminDevis />} />
              <Route path="clients" element={<AdminClients />} />
              {/* === COMMERCE === */}
              <Route path="commerce" element={<AdminCommerceHub />} />
              <Route path="boutique" element={<AdminBoutique />} />
              <Route path="photobooth-v2" element={<AdminPhotoboothV2 />} />
              <Route path="fidelite" element={<AdminFidelite />} />
              <Route path="tarifs" element={<AdminTarifs />} />
              <Route path="ce-manager" element={<AdminCE />} />
              <Route path="promo" element={<AdminPromo />} />
              <Route path="relances-auto" element={<AdminRelancesAuto />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="crm" element={<AdminCRM />} />

              {/* === OPÉRATIONS === */}
              <Route path="operations" element={<AdminOperationsHub />} />
              <Route path="livraisons" element={<AdminLivraisons />} />
              <Route path="missions" element={<AdminMissions />} />
              <Route path="documents" element={<AdminDocuments />} />
              <Route path="avis" element={<AdminAvis />} />
              {/* Routes Phase A — nouvelles pages + alias silencieux */}
              <Route path="agenda" element={<AdminAgenda />} />
              <Route path="automations" element={<AdminAutomations />} />
              <Route path="caution" element={<Navigate to="/admin" replace />} />
              <Route path="inspections" element={<AdminInspections />} />
              <Route path="live-chat" element={<AdminLiveChat />} />
              <Route path="materiel" element={<AdminMateriel />} />
              <Route path="notifications-old" element={<AdminNotifications />} />
              <Route path="opportunites" element={<AdminOpportunites />} />
              <Route path="relances" element={<AdminRelances />} />
              <Route path="white-label" element={<AdminWhiteLabel />} />
              <Route path="disponibilites" element={<AdminDisponibilites />} />
              <Route path="weekend-offres" element={<AdminDispoWeekend />} />
              <Route path="tournees" element={<AdminTournees />} />
              <Route path="exploitation" element={<AdminExploitation />} />
              <Route path="etat-materiel" element={<Navigate to="/admin/catalogue-unifie?tab=etat" replace />} />
              <Route path="caution-avancee" element={<Navigate to="/admin" replace />} />
              <Route path="sav" element={<AdminSAV />} />
              <Route path="place-to-local" element={<AdminPlaceToLocal />} />

              {/* === CONFIGURATION === */}
              <Route path="configuration" element={<AdminConfigurationHub />} />
              <Route path="gestion-produits" element={<Navigate to="/admin/catalogue-unifie?tab=prestations" replace />} />
              <Route path="packs-admin" element={<Navigate to="/admin/catalogue-unifie?tab=packs" replace />} />
              <Route path="dispo-produits" element={<Navigate to="/admin/catalogue-unifie?tab=dispo" replace />} />
              <Route path="catalogue-vente" element={<Navigate to="/admin/catalogue-unifie?tab=vente" replace />} />
              <Route path="catalogue-location" element={<Navigate to="/admin/catalogue-unifie?tab=location" replace />} />
              <Route path="partenaires-config" element={<AdminPartenairesConfig />} />
              <Route path="ia-agent" element={<AdminIAAgent />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="parametres" element={<AdminParametres />} />
              <Route path="notifications" element={<AdminNotificationsConfig />} />
              <Route path="exports" element={<AdminExports />} />
              <Route path="securite" element={<AdminSecurity />} />
              <Route path="utilisateurs" element={<AdminUsers />} />
              <Route path="ia" element={<AdminIA />} />
              <Route path="seo" element={<AdminSEO />} />

              {/* Legacy / compatibilité */}
              <Route path="fidelisation" element={<AdminFidelite />} />
            </Route>

            {/* Pilotage — Mission 3, Lot 3 (Aujourd'hui + Fiche dossier) */}
            <Route path="/pilotage" element={<PilotageLayout />}>
              <Route index element={<Navigate to="/pilotage/aujourdhui" replace />} />
              <Route path="aujourdhui" element={<Aujourdhui />} />
              <Route path="dossiers" element={<PilotageDossiers />} />
              <Route path="dossier/:id" element={<FicheDossier />} />
              <Route path="factures" element={<Factures />} />
              <Route path="partenaires" element={<Partenaires />} />
              <Route path="commissions" element={<Commissions />} />
            </Route>

            <Route path="/mes-evenements" element={<MesEvenementsPage />} />
            <Route path="/mon-evenement/:slug" element={<MonEvenementPage />} />
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="/top-ambassadeurs" element={<TopAmbassadeursPage />} />

            {/* Livreur */}
            <Route path="/livreur" element={<LivreurApp />} />
            <Route path="/mission" element={<FicheMissionPage />} />
            <Route path="/ma-reservation/:id" element={<MaReservationPage />} />
            <Route path="/avis/:token" element={<AvisPage />} />
          </Routes>
          <Toaster richColors position="top-right" />
          <LiveChatWidget />
          <ShareAndEarnWidget />
        </CartProvider>
      </AuthProvider>
      </TenantProvider>
    </BrowserRouter>
  );
}
